import {
  DocumentsApi,
  EvaluationsApi,
  FormsApi,
  SessionDetailsApi,
  TimeslotsApi,
  type HeadersValuesDTO,
  type TrainingPlanDetailsAttendeesModel,
} from "@simbelapp/order-sdk";
import { defineStore } from "pinia";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useDocumentsStore } from "~/store/documents/documents.store";
import { ItemType, MicroserviceEnum, PromiseSettledStatusEnum } from "~/utils/enums/common.enums";
import { TrainerSessionTabEnum } from "~/utils/enums/trainer-activities.enums";
import { mapEvaluationDetail } from "~/utils/helpers/evaluations.mappers";
import { mapSessionFormsTracking } from "~/utils/helpers/form-survey.mappers";
import { getErrorStatus } from "~/utils/helpers/httpErrors";
import {
  applyTrainerAttendeeRestrictions,
  toSdkFindParams,
  withTrainerAlerts,
} from "~/utils/helpers/trainer-activities";
import type { ISessionSurvey, ISessionSurveyParticipant } from "~/utils/interfaces/session-surveys-interfaces";
import type { IFindOptions, IIndexHeader, IPaginationApi } from "~/utils/interfaces/table-interfaces";
import type { ITrainerSessionStore } from "~/utils/interfaces/trainer-activities-interfaces";

/** The route returns 10 documents by default; a session has few, so the tab loads them all at once. */
const ALL_DOCUMENTS_LIMIT = 1000;

/** Load token source: a late read for a session already left, or after `$reset()`, must write nothing. */
let loadSequence = 0;

const toTrainerAttendeesPage = (
  page: IPaginationApi<TrainingPlanDetailsAttendeesModel>,
): IPaginationApi<TrainingPlanDetailsAttendeesModel> => ({
  data: (page.data ?? []).map(withTrainerAlerts),
  meta: page.meta,
});

/**
 * Trainer session page, kept apart from `useSessionDetailsStore`, whose errors redirect into the admin area.
 * It calls the admin page's routes; the backend strips what a trainer may not see. Documents have their own
 * trainer route: the admin one lists everything to a trainer who also holds an admin role.
 */
export const useTrainerSessionStore = defineStore("trainerSessionStore", {
  state: (): ITrainerSessionStore => ({
    session: null,
    timeslots: null,
    attendees: null,
    documents: null,
    timeslotAttendees: {},
    surveys: [],
    surveyRecap: null,
    responseDetail: null,
    pendingRecipientId: null,
    activeTab: TrainerSessionTabEnum.STEPS,
    isLoading: false,
    isError: false,
    errorStatus: null,
    loadToken: 0,
  }),
  getters: {
    sessionId(): string | null {
      return this.session?.sessionId ?? this.session?.id ?? null;
    },
  },
  actions: {
    /**
     * On failure, the details' HTTP status wins: that route guards access, so an out-of-reach session
     * isn't reported as an outage.
     */
    async loadSession(sessionId: string): Promise<void> {
      loadSequence += 1;
      this.loadToken = loadSequence;
      const load = this.loadToken;
      this.isLoading = true;
      this.isError = false;
      this.errorStatus = null;

      const [detailsFailure, stepsFailure] = await Promise.all([
        this.fetchSession(sessionId),
        this.fetchTimeslots(sessionId),
        this.fetchDocuments(sessionId),
        this.fetchSurveys(sessionId),
      ]);
      if (load !== this.loadToken) return;
      const failure = detailsFailure ?? stepsFailure;
      if (failure !== null) {
        this.isError = true;
        this.errorStatus = getErrorStatus(failure) ?? null;
      }
      this.isLoading = false;
    },

    /** Resolves to the failure, or null: `loadSession` turns it into the page's error state. */
    async fetchSession(sessionId: string): Promise<unknown> {
      const load = this.loadToken;
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);
        const session = await apiInstance.getTrainingPlanDetails({ itemId: sessionId, type: ItemType.SESSION });
        if (load === this.loadToken) this.session = session;
        return null;
      } catch (error) {
        return error;
      }
    },

    /** Resolves to the failure, or null, like `fetchSession`. */
    async fetchTimeslots(sessionId: string): Promise<unknown> {
      const load = this.loadToken;
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TimeslotsApi);
        const timeslots = await apiInstance.getItemTimeslots({ itemId: sessionId });
        if (load === this.loadToken) this.timeslots = timeslots;
        return null;
      } catch (error) {
        return error;
      }
    },

    async fetchAttendees(options: IFindOptions): Promise<void> {
      const sessionId = this.sessionId;
      if (!sessionId) return;
      const load = this.loadToken;

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);
        const page = await apiInstance.getTrainingPlanAttendeesFull({
          itemId: sessionId,
          itemType: ItemType.SESSION,
          ...toSdkFindParams(options),
        });
        if (load !== this.loadToken) return;
        this.attendees = toTrainerAttendeesPage({
          data: page.data,
          meta: { count: page.meta.count, totalCount: page.meta.totalCount ?? null },
        });
      } catch {
        if (load !== this.loadToken) return;
        useFeedback().error("Une erreur est survenue lors de la récupération des participants", "small");
      }
    },

    attendeeColumns(backendHeaders: HeadersValuesDTO[]): IIndexHeader[] {
      return applyTrainerAttendeeRestrictions(backendHeaders);
    },

    async fetchTimeslotAttendees(timeslotId: string, options: IFindOptions): Promise<void> {
      const load = this.loadToken;
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TimeslotsApi);
        const page = await apiInstance.getTimeslotAttendees({ timeslotId, ...toSdkFindParams(options) });
        if (load !== this.loadToken) return;
        this.timeslotAttendees = {
          ...this.timeslotAttendees,
          [timeslotId]: { data: page.data, meta: { count: page.meta.count, totalCount: page.meta.totalCount ?? null } },
        };
      } catch {
        if (load !== this.loadToken) return;
        useFeedback().error("Une erreur est survenue lors de la récupération des participants", "small");
      }
    },

    /**
     * `pageSessionId` is only passed on page load, before the details arrive; no toast then: an
     * out-of-reach session fails here too, and the page already shows its error state.
     */
    async fetchDocuments(pageSessionId?: string): Promise<void> {
      const sessionId = pageSessionId ?? this.sessionId;
      if (!sessionId) return;
      const load = this.loadToken;

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, DocumentsApi);
        const page = await apiInstance.getTrainerSessionDocuments({ sessionId, limit: ALL_DOCUMENTS_LIMIT });
        if (load !== this.loadToken) return;
        this.documents = page?.data ?? [];
      } catch {
        if (load !== this.loadToken) return;
        this.documents = [];
        if (!pageSessionId)
          useFeedback().error("Une erreur est survenue lors de la récupération des documents", "small");
      }
    },

    /**
     * One call per document, as bulk deletion is admin-only. The list is re-read even on failure, since
     * some deletions may have gone through.
     */
    async removeDocuments(documentIds: string[]): Promise<void> {
      if (!documentIds.length || !this.sessionId) return;
      const feedback = useFeedback();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, DocumentsApi);
        const results = await Promise.allSettled(
          documentIds.map((documentId) => apiInstance.removeDocument({ documentId })),
        );
        if (results.some((result) => result.status === PromiseSettledStatusEnum.REJECTED)) {
          feedback.error("Une erreur est survenue lors de la suppression", "small");
        } else {
          feedback.success("Document(s) supprimé(s) avec succès", "small");
        }
      } catch {
        feedback.error("Une erreur est survenue lors de la suppression", "small");
      }
      await this.fetchDocuments();
    },

    async openDocument(documentId: string): Promise<void> {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, DocumentsApi);
        const { documentLink } = await apiInstance.downloadDocument({ documentId });
        if (!documentLink) throw new Error("Lien de téléchargement absent");
        await navigateTo(documentLink, { external: true, open: { target: "_blank" } });
      } catch {
        feedback.error("Une erreur est survenue", "small");
      }
    },

    async generateSignInSheet(): Promise<boolean> {
      const sessionId = this.sessionId;
      if (!sessionId) return false;

      const generated = await useDocumentsStore().generateSignInSheet(sessionId);
      if (generated) await this.fetchDocuments();
      return generated;
    },

    /** No toast on failure: an out-of-reach session fails here too, and the page already shows its error state. */
    async fetchSurveys(sessionId: string): Promise<void> {
      const load = this.loadToken;
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, FormsApi);
        const { surveys, recap } = mapSessionFormsTracking(await apiInstance.getSessionFormsTracking({ sessionId }));
        if (load !== this.loadToken) return;
        this.surveys = surveys;
        this.surveyRecap = recap;
      } catch {
        if (load !== this.loadToken) return;
        this.surveys = [];
        this.surveyRecap = null;
      }
    },

    /** Only the latest request may open the panel, or clicking A then B could show A's responses. */
    async openResponseDetail(survey: ISessionSurvey, participant: ISessionSurveyParticipant): Promise<void> {
      this.pendingRecipientId = participant.id;
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, EvaluationsApi);
        const detail = await apiInstance.getEvaluation({ recipientId: participant.id });
        if (this.pendingRecipientId !== participant.id) return;
        const mapped = mapEvaluationDetail(detail);
        this.responseDetail = { survey: mapped.survey, participant: mapped.participant };
      } catch {
        if (this.pendingRecipientId !== participant.id) return;
        useFeedback().error("Erreur lors du chargement de la réponse", "small");
      }
    },

    clearResponseDetail(): void {
      this.responseDetail = null;
      // Closing the panel also disowns the in-flight request, otherwise it would reopen the panel on arrival.
      this.pendingRecipientId = null;
    },

    setActiveTab(tab: TrainerSessionTabEnum): void {
      this.activeTab = tab;
    },
  },
});
